"use client"

import { useState } from "react"
import { Link2, Check } from "lucide-react"

/**
 * The share links inside the author and share strip at the foot of an article
 * or a situation page (ArticleEndrowSection in web/modules/article-body). The
 * strip owns the hairline and the spacing above, so this renders no top margin,
 * rule or padding of its own. Buttons: Instagram, TikTok, Facebook, X, plus a
 * copy-link button. The post is auto-built from the page
 * (title + link).
 *
 * Platform reality: X and Facebook accept a web share-intent URL that prefills
 * the post. Instagram and TikTok have NO web URL that prefills a feed post, so
 * the only way to hand them content from the web is the native Web Share API
 * (the mobile share sheet, where IG/TikTok show up as targets, prefilled with
 * the title + link). On desktop (no Web Share) we copy the ready-made caption to
 * the clipboard and open the app so the user pastes — the closest the web allows
 * to auto-populating an IG/TikTok post.
 */
export function ArticleShare({ url, title }: { url: string; title: string }) {
  const [copied, setCopied] = useState(false)
  const [captionFor, setCaptionFor] = useState<"instagram" | "tiktok" | null>(null)

  const caption = `${title}\n\n${url}`
  const shareUrl = encodeURIComponent(url)
  const shareText = encodeURIComponent(title)
  const xHref = `https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`
  const facebookHref = `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}&quote=${shareText}`

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // clipboard unavailable; no-op
    }
  }

  // Instagram / TikTok have no web post-prefill. Use the native share sheet when
  // available (mobile), otherwise copy the ready-made caption and open the app.
  async function shareToApp(platform: "instagram" | "tiktok", appUrl: string) {
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share({ title, text: title, url })
      } catch {
        // user cancelled or the share was unsupported — no-op
      }
      return
    }
    try {
      await navigator.clipboard.writeText(caption)
      setCaptionFor(platform)
      setTimeout(() => setCaptionFor(null), 2500)
    } catch {
      // clipboard unavailable; still open the app below
    }
    window.open(appUrl, "_blank", "noopener,noreferrer")
  }

  const itemClass =
    "inline-flex min-h-10 items-center gap-1.5 text-[0.92rem] font-normal text-[var(--color-ink-muted)] underline decoration-[var(--color-rule-strong)] decoration-1 underline-offset-[6px] transition-colors hover:text-[var(--color-vermilion)] hover:decoration-[var(--color-vermilion)]"
  // A 44px tap area with nothing on screen moving. The items are 40px tall,
  // so the wide ones take the plain tap-target box (44px tall, their own
  // width). TikTok (43px) and X (9px) are narrower than a thumb, so each gets
  // an invisible box centred on it: 44px square for TikTok, 38px wide for X,
  // about the most the 16px gaps either side allow with a clear gap left
  // before a neighbour. Every box sits behind the words (isolate plus z -1),
  // so a tap on any item's own text always goes to that item.
  const wideHit = `tap-target ${itemClass}`
  const narrowHit = `relative after:absolute after:left-1/2 after:top-1/2 after:h-11 after:-translate-x-1/2 after:-translate-y-1/2 after:z-[-1] after:content-[''] ${itemClass}`

  return (
    <div className="isolate flex flex-wrap items-baseline gap-x-4 gap-y-0">
      <span className="text-[0.92rem] font-extrabold text-[var(--color-ink)]">
        Share
      </span>
      <button type="button" onClick={() => shareToApp("instagram", "https://www.instagram.com/")} className={wideHit}>
        {captionFor === "instagram" ? "Caption copied" : "Instagram"}
      </button>
      <button type="button" onClick={() => shareToApp("tiktok", "https://www.tiktok.com/upload")} className={`after:w-11 ${narrowHit}`}>
        {captionFor === "tiktok" ? "Caption copied" : "TikTok"}
      </button>
      <a href={facebookHref} target="_blank" rel="noopener noreferrer" className={wideHit}>
        Facebook
      </a>
      <a href={xHref} target="_blank" rel="noopener noreferrer" className={`after:w-[38px] ${narrowHit}`}>
        X
      </a>
      <button type="button" onClick={copyLink} className={wideHit}>
        {copied ? <Check size={14} aria-hidden /> : <Link2 size={14} aria-hidden />}
        {copied ? "Copied" : "Copy link"}
      </button>
    </div>
  )
}
